import type { CustomerAlternativeContext } from '../../../../../services/customerRequests/types';
import type { CostPriceAnalysis, CostPriceIngredient } from '../../../../../services/costPrice/costPriceService';
import CustomerAlternativeReview from './CustomerAlternativeReview';
import { AskCustomerButton, CustomerRequestSeed } from '../../../../customerRequests/CustomerRequestComposer';
import type { SourceRestaurantData } from "../../../../../stores/appSourceRestaurantDataStore";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    primaryText,
    secondaryText,
    greyTextColor,
    borderPrimary,
    greenColorClass,
    textBlue,
} from "../../../../../styling/stylingHelper";
import { SupplierLogo } from "../../../../ui/SupplierLogo";
import { getIngredientUnitPrice, getProductPlatformDisplay } from "./useCostPriceDetail";
import { productKey } from "../../../../../utils/reports/shared";
import { eur } from "../../../../orderhistory/utils";
import { calculateCurrentPrice } from "../../../../../utils/costPriceUtils";
import { applyWaste, resolveWaste } from "../../../../../utils/wasteUtils";
import { useOrderHistory } from "../../../../../stores/appOrderHistoryStore";
import { useAppUserDefaultStore } from "../../../../../stores/appUserDefaultStore";
import { useCostPriceStore } from "../../../../../stores/appCostPriceStore";
import { useSubrecipesStore } from "../../../../../stores/appSubrecipesStore";
import type { IngredientAlternative } from "../../../../../services/costPrice/costPriceService";
import AddIngredients, { AddIngredientSelection } from "./AddIngredients";
import ApplyAlternativeToDishesPanel from "./ApplyAlternativeToDishesPanel";
import { findAlternativeTargets } from "./alternativesPropagation";

const tempId = () => `temp-alt-${Date.now()}-${Math.random()}`;

const DeleteIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.5 5H4.16667H17.5" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.66699 5V3.33333C6.66699 2.89131 6.84259 2.46738 7.15515 2.15482C7.46771 1.84226 7.89164 1.66667 8.33366 1.66667H11.667C12.109 1.66667 12.5329 1.84226 12.8455 2.15482C13.158 2.46738 13.3337 2.89131 13.3337 3.33333V5M15.8337 5V16.6667C15.8337 17.1087 15.6581 17.5326 15.3455 17.8452C15.033 18.1577 14.609 18.3333 14.167 18.3333H5.83366C5.39164 18.3333 4.96771 18.1577 4.65515 17.8452C4.34259 17.5326 4.16699 17.1087 4.16699 16.6667V5H15.8337Z" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.33301 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11.667 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

interface PrimaryProduct {
    productName?: string;
    platform?: string;
    articleId?: string;
    customPlatformName?: string;
    // Per-portion quantity on THIS dish — the ratio base when an alternative is
    // pushed to the other dishes using the same product.
    quantityNeeded?: number;
    quantityInfo?: { stdUnit: string };
    wastePercentage?: number;
}

interface AlternativesViewProps {
    customerAlternative?: CustomerAlternativeContext;
    onReviewDone?: () => void;
    onReviewSaved?: (ingredient: CostPriceIngredient, analysis: CostPriceAnalysis) => void;
    requestContext?: CustomerRequestSeed;
    requestDisabled?: boolean;
    // The primary ingredient — used for the header + dedup (never selectable as
    // its own alternative).
    primary: PrimaryProduct;
    alternatives: IngredientAlternative[];
    restaurantId: string;
    dataOverride?: SourceRestaurantData;
    defaultWastePercentage: number;
    // Commit the full alternatives array (add / edit / remove). The caller decides
    // how to persist (cost price → server; subrecipe → local draft).
    onChange: (next: IngredientAlternative[]) => void;
    // Promote this alternative to primary (the old primary becomes an alternative
    // in its place). When omitted, no Swap affordance is shown.
    onSwap?: (alt: IngredientAlternative) => void;
    // Id of the cost-price analysis this ingredient belongs to. Supplied ONLY by the
    // cost-price detail modal, which persists every edit immediately — that is what
    // makes writing to OTHER dishes safe from here. The subrecipe modal holds a local
    // draft until Save, so it omits it and no propagation is offered.
    propagationSourceId?: string;
    onBack: () => void;
    onClose: () => void;
}

/**
 * The Alternatives editor, shown inside the ingredient side modal via the same
 * view-swap pattern as AddIngredients. Lists a primary ingredient's alternative
 * products (each with its OWN per-portion quantity/unit and optional waste %) and
 * reuses the AddIngredients picker in Orders-only mode to add more. Alternatives
 * never affect cost — only the waste-allocation engine reads them.
 */
const AlternativesView: React.FC<AlternativesViewProps> = ({
    requestContext, requestDisabled, primary, customerAlternative, onReviewSaved, onReviewDone,
    alternatives,
    restaurantId,
    dataOverride,
    defaultWastePercentage,
    onChange,
    onSwap,
    propagationSourceId,
    onBack,
    onClose,
}) => {
    const { t } = useTranslation();
    // Live pricing for each alternative (never persisted — golden rule): resolved
    // from order history exactly like primary ingredient rows are enriched.
    const liveOrders = useOrderHistory(s => s.orderHistoryList);
    const orderHistoryList = dataOverride?.orders ?? liveOrders;
    const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
    // Both lists carry full ingredients (incl. alternatives), so "which other dishes
    // use this product" is a local scan — no extra request.
    const costPriceList = useCostPriceStore(s => s.costPriceList);
    const subrecipesList = useSubrecipesStore(s => s.subrecipesList);
    const [showAdd, setShowAdd] = useState(!!customerAlternative);
    const [proposalSaved, setProposalSaved] = useState(false);
    // Id of the alternative whose "apply to other dishes" panel is open, and the
    // in-between step shown when the row has more than one to choose from.
    const [propagateForId, setPropagateForId] = useState<string | null>(null);
    const [choosingPropagate, setChoosingPropagate] = useState(false);
    // Raw input buffers while typing, committed on blur (mirrors IngredientsSection).
    const [inlineQty, setInlineQty] = useState<Record<string, string>>({});
    const [inlineWaste, setInlineWaste] = useState<Record<string, string>>({});
    // Two-step remove confirm (mirrors IngredientsSection rows): first click arms, a
    // second within 2s commits; auto-reverts otherwise.
    const [confirmingRemoveId, setConfirmingRemoveId] = useState<string | null>(null);

    useEffect(() => {
        if (!confirmingRemoveId) return;
        const timer = setTimeout(() => setConfirmingRemoveId(null), 2000);
        return () => clearTimeout(timer);
    }, [confirmingRemoveId]);

    const primaryProductKey = useMemo(
        () => productKey(primary.platform ?? "", primary.articleId, primary.productName),
        [primary]
    );

    // The primary + every existing alternative are excluded from the picker so a
    // product can't be its own or a duplicate alternative (dedup by resolver key).
    const excludeProductKeys = useMemo(() => {
        const keys = new Set<string>();
        keys.add(primaryProductKey);
        alternatives.forEach(a => keys.add(productKey(a.platform, a.articleId, a.productName)));
        return keys;
    }, [primaryProductKey, alternatives]);

    // Other dishes / subrecipes using the same primary product — the propagation
    // candidates. Empty (and the banner hidden) when the host doesn't allow it.
    const propagationTargets = useMemo(() => {
        if (!propagationSourceId) return [];
        return findAlternativeTargets(primaryProductKey, propagationSourceId, costPriceList, subrecipesList);
    }, [propagationSourceId, primaryProductKey, costPriceList, subrecipesList]);

    // Read live so quantity / waste edits made below while the panel is open are the
    // values that actually get applied.
    const propagateFor = propagateForId ? alternatives.find(a => a.id === propagateForId) : undefined;

    const openPropagate = () => {
        if (alternatives.length === 1) {
            setPropagateForId(alternatives[0].id);
            return;
        }
        setChoosingPropagate(true);
    };

    const handleAdd = (selection: AddIngredientSelection) => {
        if (selection.kind !== "product") return;
        const p = selection.product;
        // Defensive guard: never accept the primary itself or an already-added alternative,
        // even if it somehow surfaced in the picker — same resolver key the picker excludes on.
        if (excludeProductKeys.has(productKey(p.platform, p.articleId, p.name))) {
            setShowAdd(false);
            return;
        }
        const alt: IngredientAlternative = {
            id: tempId(),
            articleId: p.articleId,
            productName: p.name,
            platform: p.platform,
            customPlatformName: p.customPlatformName,
            quantityNeeded: selection.quantityNeeded,
            quantityInfo: p.quantityInfo ?? { stdAmount: 1, stdMultiplier: 1, stdUnit: "piece" },
            wastePercentage: selection.wastePercentage,
        };
        onChange([...alternatives, alt]);
        setShowAdd(false);
        // Offer the same alternative to the other dishes on this product right away —
        // the panel reads its quantity at confirm time, so tweaking it first still works.
        // Skipped when every other dish already carries this alternative: nothing to apply.
        const addedKey = productKey(p.platform, p.articleId, p.name);
        if (propagationTargets.some(target => !target.existingAlternativeKeys.includes(addedKey))) {
            setChoosingPropagate(false);
            setPropagateForId(alt.id);
        }
    };

    const commitQty = (alt: IngredientAlternative, value: string) => {
        const qty = parseFloat(value);
        if (isNaN(qty) || qty <= 0) return;
        onChange(alternatives.map(a => (a.id === alt.id ? { ...a, quantityNeeded: qty } : a)));
    };

    const commitWaste = (alt: IngredientAlternative, value: string) => {
        const trimmed = value.trim();
        let wastePercentage: number | undefined;
        if (trimmed === "") {
            wastePercentage = undefined; // inherit restaurant default
        } else {
            const parsed = parseFloat(trimmed);
            if (isNaN(parsed) || parsed < 0 || parsed > 100) return;
            wastePercentage = parsed;
        }
        onChange(alternatives.map(a => (a.id === alt.id ? { ...a, wastePercentage } : a)));
    };

    const confirmRemove = (alt: IngredientAlternative) => {
        setConfirmingRemoveId(null);
        onChange(alternatives.filter(a => a.id !== alt.id));
    };

    if (showAdd && customerAlternative && !proposalSaved && onReviewSaved) {
        return <CustomerAlternativeReview context={customerAlternative} primary={primary}
            defaultWastePercentage={defaultWastePercentage} onBack={() => setShowAdd(false)} onClose={onClose}
            onSaved={onReviewSaved} onDone={() => { setProposalSaved(true); setShowAdd(false); onReviewDone?.(); }} />;
    }

    if (showAdd) {
        return (
            <AddIngredients
                mode="alternatives"
                alternativePrimary={primary}
                restaurantId={restaurantId}
                dataOverride={dataOverride}
                onBack={() => setShowAdd(false)}
                onClose={onClose}
                onAddIngredient={handleAdd}
                excludeProductKeys={excludeProductKeys}
                defaultWastePercentage={defaultWastePercentage}
                title={t("costPrice.alternatives.addTitle")}
                addLabel={t("costPrice.alternatives.addButton")}
            />
        );
    }

    return (
        <div className="flex h-full flex-col overflow-hidden">
            {/* Header (back + close) */}
            <div className={`hidden md:flex items-center justify-between px-5 py-4 border-b bg-backgroundSecondary dark:bg-backgroundSecondary-dark ${borderPrimary}`}>
                <button
                    type="button"
                    onClick={onBack}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-borderGrey dark:border-borderGrey-dark"
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.8337 10H4.16699" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10.0003 15.8333L4.16699 9.99996L10.0003 4.16663" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-borderGrey dark:border-borderGrey-dark"
                >
                    <img src="/assets/close-modal.svg" alt="close" />
                </button>
            </div>

            <div className="flex-1 flex flex-col min-h-0 overflow-y-auto px-4 sm:px-5 py-5">
                {/* Mobile back + title */}
                <div className="flex items-center gap-4 mb-2">
                    <button type="button" onClick={onBack} className="block md:hidden">
                        <img src="/assets/arrow-narrow-left.svg" alt="arrow-narrow-left" />
                    </button>
                    <h2 className={`text-[24px] sm:text-[28px] leading-9 sm:leading-[39px] ${primaryText} font-bold`}>
                        {t("costPrice.alternatives.title")}
                    </h2>
                </div>
                {primary.productName && (
                    <p className={`text-[18px] leading-6 font-semibold ${primaryText} break-words mb-1`}>
                        {primary.productName}
                        <span className={`ml-2 text-sm font-normal ${secondaryText}`}>
                            {primary.quantityNeeded} {primary.quantityInfo?.stdUnit} · {t('costPrice.alternatives.wasteLabel')}: {resolveWaste(primary.wastePercentage, defaultWastePercentage)}
                        </span>
                    </p>
                )}
                <p className={`${greyTextColor} text-[13px] leading-5 mb-5`}>
                    {t("costPrice.alternatives.explainer")}
                </p>

                {requestContext && <AskCustomerButton className="mb-4" disabled={requestDisabled} seed={{ ...requestContext, type: requestContext.type ?? 'ingredient_alternatives', productName: primary.productName }} />}


                {/* Cross-dish propagation. The banner is the persistent entry point;
                    the panel auto-opens right after an alternative is added. */}
                {(!customerAlternative || proposalSaved) && (propagateFor ? (
                    <ApplyAlternativeToDishesPanel
                        // Remount per alternative: selection + edited quantities are that
                        // alternative's, and must not leak into the next one.
                        key={propagateFor.id}
                        primaryName={primary.productName ?? ""}
                        primaryKey={primaryProductKey}
                        alternative={propagateFor}
                        sourcePrimaryQuantity={primary.quantityNeeded ?? 0}
                        targets={propagationTargets}
                        onDone={() => setPropagateForId(null)}
                    />
                ) : choosingPropagate ? (
                    <div className="mb-5 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 space-y-2">
                        <p className={`text-[14px] font-semibold ${primaryText}`}>
                            {t("costPrice.alternatives.propagate.chooseTitle")}
                        </p>
                        {alternatives.map(alt => (
                            <button
                                key={alt.id}
                                type="button"
                                onClick={() => { setChoosingPropagate(false); setPropagateForId(alt.id); }}
                                className={`w-full rounded-lg border px-3 py-2 text-left text-[14px] font-medium ${primaryText} ${borderPrimary} bg-background dark:bg-background-dark hover:opacity-80 transition-opacity`}
                            >
                                {alt.productName}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setChoosingPropagate(false)}
                            className={`text-[13px] font-medium ${greyTextColor} hover:opacity-70`}
                        >
                            {t("costPrice.alternatives.propagate.skip")}
                        </button>
                    </div>
                ) : propagationTargets.length > 0 ? (
                    <div className={`mb-5 rounded-lg border ${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-3 py-2.5 flex items-center justify-between gap-3`}>
                        <p className={`text-[13px] leading-5 ${secondaryText} min-w-0`}>
                            {t("costPrice.alternatives.propagate.bannerText", {
                                product: primary.productName,
                                count: propagationTargets.length,
                            })}
                        </p>
                        {alternatives.length > 0 && (
                            <button
                                type="button"
                                onClick={openPropagate}
                                className={`text-[13px] font-semibold ${textBlue} hover:opacity-70 flex-shrink-0`}
                            >
                                {t("costPrice.alternatives.propagate.bannerAction")}
                            </button>
                        )}
                    </div>
                ) : null)}

                {alternatives.length === 0 ? (
                    <p className={`${greyTextColor} text-[14px] leading-[21px] mt-4 mb-6 text-center`}>
                        {t("costPrice.alternatives.empty")}
                    </p>
                ) : (
                    <div className="space-y-3">
                        {alternatives.map(alt => {
                            const platformDisplay = getProductPlatformDisplay(alt.platform, alt.customPlatformName);
                            const qtyStr = inlineQty[alt.id] ?? alt.quantityNeeded.toString();
                            const wasteStr = inlineWaste[alt.id] ?? (alt.wastePercentage !== undefined ? alt.wastePercentage.toString() : "");
                            // Live unit price + per-portion cost, mirroring IngredientsSection rows
                            // (waste folded in, live inline values while typing).
                            const { currentPrice } = calculateCurrentPrice(
                                { articleId: alt.articleId, productName: alt.productName, platform: alt.platform } as any,
                                orderHistoryList, undefined, showExVatOrders
                            );
                            const unitPrice = getIngredientUnitPrice({ currentPrice, quantityInfo: alt.quantityInfo });
                            const resolvedWaste = resolveWaste(
                                wasteStr.trim() === "" ? undefined : (parseFloat(wasteStr) || 0),
                                defaultWastePercentage
                            );
                            const altCost = currentPrice ? applyWaste(parseFloat(qtyStr) || 0, resolvedWaste) * currentPrice : 0;
                            return (
                                <div key={alt.id} className={`rounded-lg border py-[11px] pl-3 pr-4 flex flex-col bg-backgroundSecondary dark:bg-backgroundSecondary-dark ${borderPrimary}`}>
                                    {/* Row 1: name + remove */}
                                    <div className="flex justify-between items-start mb-2">
                                        <p className={`text-[16px] leading-6 font-semibold tracking-[-0.16px] ${primaryText} min-w-0 truncate`}>
                                            {alt.productName}
                                        </p>
                                        <div className={`flex items-center gap-2 flex-shrink-0 ml-2 ${customerAlternative ? "hidden" : ""}`}>
                                            {onSwap && confirmingRemoveId !== alt.id && (
                                                <button
                                                    type="button"
                                                    onClick={() => onSwap(alt)}
                                                    className="h-8 px-3 text-[14px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                                                >
                                                    {t("costPrice.alternatives.swapButton")}
                                                </button>
                                            )}
                                            {confirmingRemoveId === alt.id ? (
                                                <button
                                                    type="button"
                                                    onClick={() => confirmRemove(alt)}
                                                    className="h-8 px-3 text-[14px] font-semibold bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white dark:text-white rounded-lg transition-colors"
                                                >
                                                    {t("costPrice.subrecipes.removeAction")}
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => setConfirmingRemoveId(alt.id)}
                                                    className="p-1 hover:opacity-70 transition-opacity"
                                                >
                                                    <DeleteIcon />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 2: supplier */}
                                    {(platformDisplay.showLogo || platformDisplay.displayName) && (
                                        <div className="flex items-center justify-between gap-3">
                                            <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                                {t("costPrice.alternatives.supplierLabel")}
                                            </p>
                                            {platformDisplay.showLogo && platformDisplay.platformForLogo ? (
                                                <SupplierLogo
                                                    platform={platformDisplay.platformForLogo}
                                                    size="medium"
                                                    alt={platformDisplay.displayName}
                                                    className="object-contain"
                                                />
                                            ) : (
                                                <span className={`${secondaryText} text-[14px] leading-5 font-medium`}>{platformDisplay.displayName}</span>
                                            )}
                                        </div>
                                    )}

                                    {/* Row 2b: live unit price (converted: /kg, /L, /pc) */}
                                    {unitPrice && (
                                        <div className="flex items-center justify-between gap-3 mt-2">
                                            <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                                {t("costPrice.alternatives.unitPriceLabel")}
                                            </p>
                                            <p className={`${secondaryText} text-[14px] leading-5 font-medium`}>
                                                {eur(unitPrice.value)}/{unitPrice.unit}
                                            </p>
                                        </div>
                                    )}

                                    {/* Row 3: per-portion quantity in the alternative's OWN unit */}
                                    <div className="flex items-center justify-between gap-3 mt-2">
                                        <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                            {t("costPrice.alternatives.quantityLabel")}
                                        </p>
                                        <div className="flex items-center gap-1">
                                            <input
                                                disabled={!!customerAlternative}
                                                type="number" inputMode="decimal" min={0} step="any"
                                                value={qtyStr}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) setInlineQty(prev => ({ ...prev, [alt.id]: value }));
                                                }}
                                                onBlur={() => { if (inlineQty[alt.id] !== undefined) commitQty(alt, inlineQty[alt.id]); }}
                                                onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                                                className="w-16 text-right bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded px-2 py-0.5 text-[14px] leading-5 font-medium text-[#0C111D] dark:text-textPrimary-dark outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                            />
                                            <span className={`text-[14px] leading-5 font-medium ${secondaryText}`}>
                                                {alt.quantityInfo.stdUnit}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Row 4: waste % — placeholder shows the restaurant default */}
                                    <div className="flex items-center justify-between gap-3 mt-2">
                                        <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                            {t("costPrice.alternatives.wasteLabel")}
                                        </p>
                                        <div className="flex items-center gap-1">
                                            <input
                                                disabled={!!customerAlternative}
                                                type="number" inputMode="decimal" min={0} max={100} step="0.5"
                                                value={wasteStr}
                                                placeholder={defaultWastePercentage.toString()}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) setInlineWaste(prev => ({ ...prev, [alt.id]: value }));
                                                }}
                                                onBlur={() => { if (inlineWaste[alt.id] !== undefined) commitWaste(alt, inlineWaste[alt.id]); }}
                                                onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                                                className="w-16 text-right bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded px-2 py-0.5 text-[14px] leading-5 font-medium text-[#0C111D] dark:text-textPrimary-dark outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                            />
                                            <span className={`text-[14px] leading-5 font-medium ${secondaryText}`}>%</span>
                                        </div>
                                    </div>

                                    {/* Row 5: per-portion cost with this alternative (waste folded in) */}
                                    <div className="flex justify-between items-center gap-2 mt-2">
                                        <p className={`text-[14px] leading-[21px] font-normal tracking-[-0.14px] ${greyTextColor}`}>
                                            {t("costPrice.alternatives.costLabel")}
                                        </p>
                                        <p className={`text-[14px] leading-5 font-medium ${textBlue}`}>{eur(altCost)}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <button
                    type="button"
                    className={`mt-6 flex w-full items-center justify-center h-11 text-[16px] leading-6 font-semibold gap-2 ${greenColorClass} rounded-lg`}
                    onClick={() => setShowAdd(true)}
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 4.16699V15.8337" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.1665 10H15.8332" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {t("costPrice.alternatives.addButton")}
                </button>
            </div>
        </div>
    );
};

export default AlternativesView;
